/**
 * @file
 * At The Tanks
 */

import castArray from 'lodash/castArray';
import * as React from 'react';

import meta from './meta';

// Config
import { SHARED_REFRESH_RATE } from '../../config';

// Components
import AnglerList from '../../components/AnglerList/AnglerList';
import BassComponent from '../../components/BassComponent/BassComponent';
import Loader from '../../components/Loader/Loader';
import Message from '../../components/Message/Message';

// Utils
import * as api from '../../util/api';

// Types
import { RefreshRate } from '../../types';
import { isEOD, ITankAnglerData, MaybeITankAnglerData } from './types';

interface IProps {
  // Pretty sure this is the tournament id.
  tid: number;
  // Optional prop. Defaults to 30000
  refreshRate?: RefreshRate;
  // Not sure this is really needed. Always seems to be 'At The Tanks'
  title?: string;
  // Active is set as an initial prop as well
  active: boolean;
}

interface IState {
  // Set to true on successful data fetch that is not EOD
  active: boolean;
  // Array of ITankAngler.NAME_FONTNAME
  data: string[];
  // Indicate loading data
  loading: boolean;
}

export class AtTheTanks extends React.Component<IProps, IState> {
  public static defaultProps = {
    refreshRate: SHARED_REFRESH_RATE,
    title: 'At The Tanks',
  };

  public state = {
    active: this.props.active, // Active tournament indicator
    data: [],
    loading: true,
  };

  public fetchData = () => (result: MaybeITankAnglerData) => {
    // We get data, no data, or EOD signal
    if (!result || isEOD(result)) {
      this.setState({
        active: false,
        loading: false,
      });
      return;
    }

    this.setState({
      active: true,
      data: this.checkResult(result),
      loading: false,
    });
  };

  /**
   * Deal with the JSON object returning either an array of ITankAnglers
   * or a single ITankAngler object. Returns an array of
   * ITankAngler.NAME_FONTNAME to be passed to <AnglerList />
   */
  public checkResult = (result: ITankAnglerData): string[] => castArray(result).map(({ FontName }) => FontName);

  public async componentDidMount() {
    // Props show inactive, don't even request
    if (!this.props.active) {
      this.setState({
        active: this.props.active,
        loading: false,
      });
      return;
    }

    // Shared settings
    const apiSettings = {
      callback: this.fetchData(),
      uri: `/wp-json/data/v1/tournament/leaderboard/at-the-tanks/?tournament_id=${this.props.tid}`,
    };

    // Poll if active
    if (this.state.active) {
      await api.subscribe({
        ...apiSettings,
        rate: this.props.refreshRate || SHARED_REFRESH_RATE,
      });
    }
    // Otherwise make single request
    else {
      await api.get(apiSettings);
    }
  }

  public render() {
    const { active, data, loading } = this.state;

    return (
      <BassComponent meta={meta}>
        <div
          className="at-the-tanks leaderboard-list"
          data-testid="at-the-tanks leaderboard-list"
        >
          <h2 className="at-the-tanks__title leaderboard-list__title">
            {this.props.title}
          </h2>

          <Loader loading={loading} />

          {!loading && active && Array.isArray(data) && <AnglerList list={data.slice(0, 5)} />}

          <Message
            message="No anglers at the tank."
            customClass="on-stage__message"
            shouldShow={!loading && !active}
          />
        </div>
      </BassComponent>
    );
  }
}

export default AtTheTanks;
